import { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";

const MAX_PLAY_TOKENS = 10;

function formatDate(value) {
  if (!value) return "—";

  const date = value.toDate ? value.toDate() : new Date(value);

  return date.toLocaleString("es-CO", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function getDateValue(value) {
  if (!value) return 0;

  if (value.toDate) {
    return value.toDate().getTime();
  }

  return new Date(value).getTime() || 0;
}

function PlayTokenCircle({ value = 0, max = MAX_PLAY_TOKENS }) {
  const safeValue = Math.max(0, Math.min(Number(value) || 0, max));
  const percentage = (safeValue / max) * 100;

  return (
    <div
      className="token-circle"
      style={{
        background: `conic-gradient(
          var(--color-primary) ${percentage}%,
          #e5e7eb ${percentage}%
        )`,
      }}
      title={`${safeValue}/${max} play tokens`}
    >
      <div className="token-circle__inner">
        <strong>{safeValue}</strong>
        <span>/{max}</span>
      </div>
    </div>
  );
}

function SortButton({ label, field, sortConfig, onSort }) {
  const isActive = sortConfig.field === field;
  const arrow = isActive ? (sortConfig.direction === "asc" ? "↑" : "↓") : "↕";

  return (
    <button
      type="button"
      className={`sort-button ${isActive ? "sort-button--active" : ""}`}
      onClick={() => onSort(field)}
    >
      {label} <span>{arrow}</span>
    </button>
  );
}

function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [searchText, setSearchText] = useState("");
  const [sortConfig, setSortConfig] = useState({
    field: "lastLoginAt",
    direction: "desc",
  });

  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    displayName: "",
    shopCoinsBalance: 0,
    lifeGuardBalance: 0,
    timeFreezeBalance: 0,
    playTokenBalance: 0,
  });

  async function loadUsers() {
    try {
      setError("");
      setLoadingUsers(true);

      const usersQuery = query(
        collection(db, "users"),
        orderBy("lastLoginAt", "desc")
      );

      const snapshot = await getDocs(usersQuery);

      const data = snapshot.docs.map((docItem) => ({
        id: docItem.id,
        ...docItem.data(),
      }));

      setUsers(data);
    } catch (err) {
      console.error("Error cargando usuarios:", err);
      setError("No se pudieron cargar los usuarios.");
    } finally {
      setLoadingUsers(false);
    }
  }

  useEffect(() => {
    loadUsers();
  }, []);

  const filteredAndSortedUsers = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    const filtered = users.filter((user) => {
      const name = String(user.displayName || "").toLowerCase();
      const email = String(user.email || "").toLowerCase();

      return (
        name.includes(normalizedSearch) ||
        email.includes(normalizedSearch)
      );
    });

    const sorted = [...filtered].sort((a, b) => {
      let aValue;
      let bValue;

      switch (sortConfig.field) {
        case "displayName":
          aValue = String(a.displayName || "").toLowerCase();
          bValue = String(b.displayName || "").toLowerCase();
          break;

        case "email":
          aValue = String(a.email || "").toLowerCase();
          bValue = String(b.email || "").toLowerCase();
          break;

        case "lastLoginAt":
          aValue = getDateValue(a.lastLoginAt);
          bValue = getDateValue(b.lastLoginAt);
          break;

        case "shopCoinsBalance":
          aValue = Number(a.shopCoinsBalance) || 0;
          bValue = Number(b.shopCoinsBalance) || 0;
          break;

        case "lifeGuardBalance":
          aValue = Number(a.lifeGuardBalance) || 0;
          bValue = Number(b.lifeGuardBalance) || 0;
          break;

        case "timeFreezeBalance":
          aValue = Number(a.timeFreezeBalance) || 0;
          bValue = Number(b.timeFreezeBalance) || 0;
          break;

        case "playTokenBalance":
          aValue = Number(a.playTokenBalance) || 0;
          bValue = Number(b.playTokenBalance) || 0;
          break;

        default:
          aValue = "";
          bValue = "";
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }

      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }

      return 0;
    });

    return sorted;
  }, [users, searchText, sortConfig]);

  function handleSort(field) {
    setSortConfig((prev) => {
      if (prev.field === field) {
        return {
          field,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        field,
        direction: "asc",
      };
    });
  }

  function openEditModal(user) {
    setEditingUser(user);

    setEditForm({
      displayName: user.displayName || "",
      shopCoinsBalance: user.shopCoinsBalance ?? 0,
      lifeGuardBalance: user.lifeGuardBalance ?? 0,
      timeFreezeBalance: user.timeFreezeBalance ?? 0,
      playTokenBalance: user.playTokenBalance ?? 0,
    });
  }

  function closeEditModal() {
    setEditingUser(null);

    setEditForm({
      displayName: "",
      shopCoinsBalance: 0,
      lifeGuardBalance: 0,
      timeFreezeBalance: 0,
      playTokenBalance: 0,
    });
  }

  function handleEditChange(field, value) {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function refillPlayTokensInForm() {
    setEditForm((prev) => ({
      ...prev,
      playTokenBalance: MAX_PLAY_TOKENS,
    }));
  }

  async function saveUserChanges(e) {
    e.preventDefault();

    if (!editingUser) return;

    const normalizedData = {
      displayName: editForm.displayName.trim(),
      shopCoinsBalance: Math.max(0, Number(editForm.shopCoinsBalance) || 0),
      lifeGuardBalance: Math.max(0, Number(editForm.lifeGuardBalance) || 0),
      timeFreezeBalance: Math.max(0, Number(editForm.timeFreezeBalance) || 0),
      playTokenBalance: Math.max(
        0,
        Math.min(Number(editForm.playTokenBalance) || 0, MAX_PLAY_TOKENS)
      ),
    };

    try {
      setSaving(true);

      const userRef = doc(db, "users", editingUser.id);

      await updateDoc(userRef, {
        ...normalizedData,
        playTokenRegenAt:
          normalizedData.playTokenBalance >= MAX_PLAY_TOKENS
            ? null
            : editingUser.playTokenRegenAt ?? null,
      });

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === editingUser.id
            ? {
                ...user,
                ...normalizedData,
                playTokenRegenAt:
                  normalizedData.playTokenBalance >= MAX_PLAY_TOKENS
                    ? null
                    : user.playTokenRegenAt ?? null,
              }
            : user
        )
      );

      closeEditModal();
    } catch (err) {
      console.error("Error actualizando usuario:", err);
      alert("No se pudo actualizar el usuario.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="admin-section-card">
      <div className="section-header">
        <div>
          <h2>Usuarios</h2>
          <p>Listado general de usuarios registrados.</p>
        </div>

        <div className="section-actions">
          <button className="secondary-button" onClick={loadUsers}>
            Actualizar
          </button>

          <span className="section-count">
            {filteredAndSortedUsers.length} / {users.length} usuarios
          </span>
        </div>
      </div>

      <div className="users-toolbar">
        <div className="search-box users-search-box">
          <span>🔎</span>
          <input 
            type="text"
            placeholder="Buscar por nombre o correo..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        {searchText && (
          <button
            type="button"
            className="secondary-button"
            onClick={() => setSearchText("")}
          >
            Limpiar
          </button>
        )}
      </div>

      {loadingUsers ? (
        <p>Cargando usuarios...</p>
      ) : error ? (
        <p className="dashboard-error">{error}</p>
      ) : (
        <div className="table-wrapper">
          <table className="admin-table users-table">
            <thead>
              <tr>
                <th>
                  <SortButton
                    label="Usuario"
                    field="displayName"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                </th>

                <th>
                  <SortButton
                    label="Email"
                    field="email"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                </th>

                <th>
                  <SortButton
                    label="Último login"
                    field="lastLoginAt"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                </th>

                <th>
                  <div className="resource-sort-header">
                    <span>Recursos</span>

                    <div className="resource-sort-buttons">
                      <SortButton
                        label="💰"
                        field="shopCoinsBalance"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />

                      <SortButton
                        label="🛡️"
                        field="lifeGuardBalance"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />

                      <SortButton
                        label="❄️"
                        field="timeFreezeBalance"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    </div>
                  </div>
                </th>

                <th>
                  <SortButton
                    label="Fichas de juego"
                    field="playTokenBalance"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                </th>

                <th>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {filteredAndSortedUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="user-cell">
                      <div className="user-name-row">
                        <strong>{user.displayName || "Sin nombre"}</strong>

                        {user.role === "admin" && (
                          <span className="admin-mark">Admin</span>
                        )}
                      </div>

                      <span>{user.id}</span>
                    </div>
                  </td>

                  <td>{user.email || "—"}</td>

                  <td>{formatDate(user.lastLoginAt)}</td>

                  <td>
                    <ul className="resource-list">
                      <li>
                        <span className="resource-emoji">💰</span>
                        <strong>{user.shopCoinsBalance ?? 0}</strong>
                      </li>

                      <li>
                        <span className="resource-emoji">🛡️</span>
                        <strong>{user.lifeGuardBalance ?? 0}</strong>
                      </li>

                      <li>
                        <span className="resource-emoji">❄️</span>
                        <strong>{user.timeFreezeBalance ?? 0}</strong>
                      </li>
                    </ul>
                  </td>

                  <td>
                    <PlayTokenCircle value={user.playTokenBalance ?? 0} />
                  </td>

                  <td>
                    <button
                      className="small-button small-button--dark"
                      onClick={() => openEditModal(user)}
                    >
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredAndSortedUsers.length === 0 && (
            <p className="empty-state">
              No hay usuarios que coincidan con la búsqueda.
            </p>
          )}
        </div>
      )}

      {editingUser && (
        <div className="modal-backdrop">
          <div className="edit-modal">
            <div className="edit-modal__header">
              <div>
                <h3>Editar usuario</h3>
                <p>{editingUser.email}</p>
              </div>

              <button
                type="button"
                className="modal-close-button"
                onClick={closeEditModal}
                disabled={saving}
              >
                ×
              </button>
            </div>

            <form onSubmit={saveUserChanges} className="edit-form">
              <label>
                Nombre
                <input
                  type="text"
                  value={editForm.displayName}
                  onChange={(e) =>
                    handleEditChange("displayName", e.target.value)
                  }
                />
              </label>

              <div className="edit-form-grid">
                <label>
                  Shop Coins
                  <input
                    type="number"
                    value={editForm.shopCoinsBalance}
                    onChange={(e) =>
                      handleEditChange("shopCoinsBalance", e.target.value)
                    }
                    min="0"
                  />
                </label>

                <label>
                  LifeGuard
                  <input
                    type="number"
                    value={editForm.lifeGuardBalance}
                    onChange={(e) =>
                      handleEditChange("lifeGuardBalance", e.target.value)
                    }
                    min="0"
                  />
                </label>

                <label>
                  Time Freeze
                  <input
                    type="number"
                    value={editForm.timeFreezeBalance}
                    onChange={(e) =>
                      handleEditChange("timeFreezeBalance", e.target.value)
                    }
                    min="0"
                  />
                </label>

                <label>
                  Play Tokens
                  <div className="token-edit-row">
                    <input
                      type="number"
                      value={editForm.playTokenBalance}
                      onChange={(e) =>
                        handleEditChange("playTokenBalance", e.target.value)
                      }
                      min="0"
                      max={MAX_PLAY_TOKENS}
                    />

                    <button
                      type="button"
                      className="small-button"
                      onClick={refillPlayTokensInForm}
                      disabled={
                        Number(editForm.playTokenBalance) >= MAX_PLAY_TOKENS
                      }
                    >
                      Refill
                    </button>
                  </div>
                </label>
              </div>

              <div className="edit-preview">
                <span>Play Tokens</span>
                <PlayTokenCircle value={editForm.playTokenBalance} />
              </div>

              <div className="edit-modal__actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={closeEditModal}
                  disabled={saving}
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="primary-action-button"
                  disabled={saving}
                >
                  {saving ? "Guardando..." : "Guardar cambios"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}

export default UsersPage;